'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { Message } from '@/lib/api/types'
import { Bot, User } from 'lucide-react'

export interface ChatMessageProps {
  message: Message
  isStreaming?: boolean
  agentName?: string
  agentAvatarUrl?: string
}

/**
 * Renders a single chat message with role-based styling
 * Supports markdown-like formatting for code blocks and inline code
 */
export function ChatMessage({
  message,
  isStreaming = false,
  agentName = 'Assistant',
  agentAvatarUrl,
}: ChatMessageProps) {
  const isUser = message.role === 'User'
  const isAssistant = message.role === 'Assistant'

  return (
    <div
      className={cn(
        'flex gap-3 p-4',
        isUser && 'flex-row-reverse'
      )}
    >
      {/* Avatar */}
      <Avatar className="h-8 w-8 shrink-0">
        {isAssistant && agentAvatarUrl ? (
          <AvatarImage src={agentAvatarUrl} alt={agentName} />
        ) : null}
        <AvatarFallback
          className={cn(
            isUser
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
          )}
        >
          {isUser ? (
            <User className="h-4 w-4" />
          ) : (
            <Bot className="h-4 w-4" />
          )}
        </AvatarFallback>
      </Avatar>

      {/* Message content */}
      <div
        className={cn(
          'flex max-w-[80%] flex-col gap-1',
          isUser && 'items-end'
        )}
      >
        {/* Role label */}
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {isUser ? 'You' : agentName}
        </span>

        {/* Message bubble */}
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm',
            isUser
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
          )}
        >
          {isStreaming ? (
            <div className="flex items-center gap-2">
              <MessageContent content={message.content} />
              <TypingIndicator />
            </div>
          ) : (
            <MessageContent content={message.content} />
          )}
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>
            {new Date(message.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {message.tokenCount && !isUser && (
            <span className="text-gray-400">
              · {message.tokenCount} tokens
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Renders message content with basic markdown support
 * Handles code blocks, inline code, bold, and italic
 */
function MessageContent({ content }: { content: string }) {
  // Simple markdown parsing for code blocks and inline code
  const renderContent = React.useMemo(() => {
    if (!content) return null

    // Split by code blocks first
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g
    const processedContent = content.replace(codeBlockRegex, (_fullMatch, lang, code) => {
      return `[[CODE_BLOCK:${lang || ''}:${btoa(encodeURIComponent(code))}]]`
    })

    // Split by our placeholder and render
    const segments = processedContent.split(/(\[\[CODE_BLOCK:.*?:.*?\]\])/g)
    
    return segments.map((segment, index) => {
      const codeBlockMatch = segment.match(/\[\[CODE_BLOCK:(.*?):(.*?)\]\]/)
      if (codeBlockMatch && codeBlockMatch[2]) {
        const lang = codeBlockMatch[1] ?? ''
        const encodedCode = codeBlockMatch[2]
        let code: string
        try {
          code = decodeURIComponent(atob(encodedCode))
        } catch {
          code = encodedCode // Fallback if decoding fails
        }
        return (
          <pre
            key={index}
            className="my-2 overflow-x-auto rounded-lg bg-gray-900 p-3 text-sm text-gray-100"
          >
            {lang && (
              <div className="mb-2 text-xs text-gray-400">{lang}</div>
            )}
            <code>{code.trim()}</code>
          </pre>
        )
      }

      // Process inline formatting
      return (
        <span key={index}>
          {renderInlineFormatting(segment)}
        </span>
      )
    })
  }, [content])

  return <div className="whitespace-pre-wrap break-words">{renderContent}</div>
}

/**
 * Renders inline formatting (code, bold, italic)
 */
function renderInlineFormatting(text: string): React.ReactNode[] {
  if (!text) return []
  
  // Handle inline code
  const inlineCodeRegex = /`([^`]+)`/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match

  while ((match = inlineCodeRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    // Add the code span
    parts.push(
      <code
        key={match.index}
        className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs dark:bg-gray-700"
      >
        {match[1]}
      </code>
    )
    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Animated typing indicator shown during streaming
 */
export function TypingIndicator() {
  return (
    <span className="inline-flex items-center gap-0.5">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
    </span>
  )
}
